import styles from "../drones-page.module.scss";
import { Card, Flex, Select, Space, Tabs, Tag, Typography, Button } from "antd";
import { IDrone } from "@drone-parts.garphild.pro/interfaces";
import { useEffect, useState } from "react";
import QRCode from 'qrcode';
import {
  CheckCircleFilled,
  CheckCircleOutlined,
  EditFilled,
  PlusOutlined
} from "@ant-design/icons";
import templatesStorage from "../../../storages/templatesStorage";
import { useAtom } from "jotai/index";
import { availableStatusesListAtom } from "../state";
import { PRODUCT_TYPE_MAP } from "../product-types";
import { PartsTab, OtherPartsTab, ChecklistTab, InfoTab } from "./drone-card/tabs";


export interface IDroneCardProps {
  drone: IDrone;
  onChangeStatus: (status: string) => void;
  onChangeIncome: (income: number) => void;
  onPartUpdated?: (drone: IDrone) => void;
  actions: {
    edit: () => void;
    delete: () => void;
    inventarize?: () => void;
  };
}

export interface IPartsTabProps {
  drone: IDrone;
  dronePartTypes: Record<string, any[]>;
  mainParts: string[];
  mainDroneParts: any[];
  onPartUpdated?: (drone: IDrone) => void;
  onShowAddPart?: (show: boolean) => void;
  showAddPart?: boolean;
}

function DroneCard(props: IDroneCardProps) {
  const {
    drone,
    onChangeStatus,
    actions,
  } = props;

  const [currencyRateUahToUsd, setCurrencyRateUahToUsd] = useState<number>(43);
  const [qrCode, setQrCode] = useState<string | undefined>(undefined);
  const productTypeConfig = PRODUCT_TYPE_MAP[drone.itemType || 'drone'];
  const mainParts: string[] = [...new Set<string>([...(productTypeConfig?.mainParts || []), ...(drone?.parts?.map(p => p.key) || [])])];
  const [statusEditMode, setStatusEditMode] = useState(false);
  const [statusesList, setStatusesList] = useAtom(availableStatusesListAtom);
  const [showAddPart, setShowAddPart] = useState<boolean>(false);

  let timeout: NodeJS.Timeout | null = null;
  const handleChangeIncome = (newIncome: number) => {
    const income = isNaN(newIncome) ? undefined : newIncome;
    if (timeout) {
      clearTimeout(timeout);
    }
    if (income !== undefined) {
      timeout = setTimeout(() => {
        props.onChangeIncome(income);
        timeout = null;
      }, 300);
    }
  }

  useEffect(() => {
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    }
  }, []);

  const cardStyle: Record<string, string | number> = {
    backgroundColor: 'white',
    marginTop: '1rem',
    height: 600,
  };
  if (drone.income && drone.income - drone.total < 50) {
    cardStyle.backgroundColor = 'lightgray'
  } else if (drone.income && drone.income - drone.total < 10) {
    cardStyle.backgroundColor = 'pink'
  }

  const hasNotReceived = drone.parts.some((part) => {
    return part.item && !part.item?.received;
  });
  if (hasNotReceived) {
    cardStyle.backgroundColor = 'yellow';
  }

  const dronePartTypes = drone.parts.reduce((acc, part) => {
    const partTypes = part.item?.partTypes;
    if (partTypes?.length) {
      const pt = partTypes[0].split(':')?.[0] || partTypes[0];
      acc[pt] = acc[pt] || [];
      acc[pt].push(part);
    } else {
      acc['other'] = acc['other'] || [];
      acc['other'].push(part);
    }
    return acc;
  }, {} as Record<string, any[]>);

  console.log("dronePartTypes", dronePartTypes);

  const mainDroneParts = mainParts.map((partKey) => {
    const part = drone.parts.find((part) => part.key.toLowerCase() === partKey.toLowerCase());
    const {
      shortName,
      itemText,
      received,
      orderStatus,
      trackingStatus,
      orderObjectId,
    } = part?.item || {};
    return {
      ...part,
      partKey,
      shortName,
      itemText,
      received,
      orderStatus,
      trackingStatus,
      orderObjectId,
    };
  }).filter(Boolean);

  const qrCodeData = []; //mainDroneParts.map((part) => part?.shortName?.split(/\s/).filter(m => !!m.trim()).slice(0, 2).join(" ")).slice(0,15);
  qrCodeData.unshift(drone._id);
  qrCodeData.unshift(drone.droneId.replace(productTypeConfig?.prefix || 'PROD', (productTypeConfig?.prefix || 'PROD').substring(0, 2)));


  if (!qrCode) {
    QRCode.toDataURL(
      qrCodeData.join("\n"),
      {
        errorCorrectionLevel: 'L',
        scale: 4,
        width: 192,
      }
    )
      .then((url: string) => {
        setQrCode(url);
      })
      .catch((err: any) => {
        console.error(err)
      })
  }

  const STATUS_COLORS: Record<string, string> = {
    "draft": "gray",
    "sent": "blue",
    "have problems": "red",
  }

  const droneCostUsd = Math.ceil((drone?.total + 50) / 10) * 10;
  const droneCostUah = Math.ceil((droneCostUsd * currencyRateUahToUsd) / 100) * 100;
  const template = templatesStorage.templates.find((t) => t._id === drone.template);

  return (
    <Card
      style={cardStyle}
      className="drone-card"
      title={
        <>
          <br />
          <Typography>{drone.droneId}</Typography>
          {drone.model && <Typography>Model: {drone.model}</Typography>}
          {template && <Typography>Template: {template.title}</Typography>}
          <Typography>
            Price: ${droneCostUsd.toFixed(2)} / &#8372;{droneCostUah.toFixed(2)}
            {drone.income || drone.status === 'sent' ? (
              <small>{((drone.income || 0) - drone.total).toFixed(2)}$)</small>
            ) : ''}
          </Typography>
          {drone.updatedAt && (
            <Typography>updated: {new Date(drone.updatedAt).toISOString()}</Typography>
          )}
        </>
      }
      extra={
        <>
          <Flex>
            <Space direction="vertical" align="center" style={{ padding: 4, boxShadow: '2px 2px 16px rgba(0,0,0,.25)' }}>
              {statusEditMode ? (
                <Select
                  value={drone.status || "draft"}
                  style={{ width: '100%' }}
                  size="small"
                  onChange={(status) => {
                    if (status && status !== drone.status) {
                      onChangeStatus(status);
                      setStatusEditMode(false);
                    }
                  }}
                  options={statusesList || []}
                  allowClear={true}
                  onClear={() => setStatusEditMode(false)}
                />
              ) : (
                <Tag
                  style={{ fontSize: "0.8em", color: STATUS_COLORS[drone.status || ''] }}
                  onClick={() => setStatusEditMode(true)}
                  title={drone.status === 'have problems' ? drone.comments?.map(c => c.text)?.join(" ") : ''}
                >
                  {drone.status}
                </Tag>
              )}
              <Space>
                <Tag title="Edit" onClick={() => actions.edit()}><EditFilled /></Tag>
                <Tag 
                  title="Inventarization status" 
                  onClick={() => actions.inventarize?.()} 
                  style={{ color: drone.inventarized ? "green" : "red" }}
                >
                  {drone.inventarized ? <CheckCircleFilled /> : <CheckCircleOutlined />}
                </Tag>
                <Button 
                  type="text" 
                  size="small" 
                  icon={<PlusOutlined />}
                  title="Add component"
                  onClick={() => setShowAddPart(!showAddPart)}
                />
              </Space>
            </Space>
          </Flex>
        </>
      }
    >
      <Space direction="vertical" style={{ width: '100%', height: '100%', position: 'relative' }}>
        <Tabs 
          defaultActiveKey="parts" 
          size="small"
          style={{ width: '100%', height: '100%', position: 'relative' }}
          tabBarStyle={{ marginBottom: 8, fontSize: '0.8rem', marginTop: 0 }}
          tabBarGutter={4}
          className="compactTabs"
        >
          <Tabs.TabPane tab="Parts" key="parts">
            <PartsTab 
              drone={drone} 
              dronePartTypes={dronePartTypes} 
              mainParts={mainParts} 
              mainDroneParts={mainDroneParts}
              partOrder={productTypeConfig?.partOrder || []}
              onPartUpdated={props.onPartUpdated}
              onShowAddPart={setShowAddPart}
              showAddPart={showAddPart}
            />
          </Tabs.TabPane>
          
          <Tabs.TabPane tab="Other parts" key="other">
            <OtherPartsTab 
              drone={drone} 
              mainParts={mainParts} 
              statusesList={statusesList} 
              onChangeStatus={onChangeStatus} 
              handleChangeIncome={handleChangeIncome} 
            />
          </Tabs.TabPane>
          
          {productTypeConfig?.showChecklist !== false && (
            <Tabs.TabPane 
              tab={
                <span>
                  Checklist 
                  {drone.checklist?.filter((cli) => cli.value)?.length !== drone.checklist?.length && (
                    <span style={{ color: 'red' }}>
                      ({drone.checklist?.filter((cli) => cli.value)?.length}/{drone.checklist?.length})
                    </span>
                  )}
                </span>
              } 
              key="checklist"
            >
              <ChecklistTab drone={drone} />
            </Tabs.TabPane>
          )}
          
          <Tabs.TabPane 
            tab={
              <span>
                Info
                {drone.comments?.length ? <span style={{ color: 'red' }}> (!)</span> : null}
              </span>
            } 
            key="info"
          >
            <InfoTab drone={drone} qrCode={qrCode} />
          </Tabs.TabPane>
        </Tabs>
      </Space>
    </Card>
  );
}

export default DroneCard;
